import { isPreviewMode } from "@innoclouds/shared";
import * as Sentry from "@sentry/vue";
import type { App } from "vue";

const RELOAD_KEY = "qr-chunk-reload";
const RELOAD_COOLDOWN_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
	"Failed to fetch dynamically imported module",
	"Importing a module script failed",
	"Loading chunk",
	"ChunkLoadError",
	"Loading CSS chunk",
];

const HARMLESS_ERROR_PATTERNS = [
	{ pattern: "reading 'insertBefore'", tag: "vue_teleport_race" },
	{ pattern: "reading 'scrollSize'", tag: "virtua_unmount_race" },
	{ pattern: "reading 'viewportSize'", tag: "virtua_unmount_race" },
	{ pattern: "reading 'scrollOffset'", tag: "virtua_unmount_race" },
	{ pattern: "without an in-progress transaction", tag: "idb_stale_transaction" },
	{ pattern: "ResizeObserver loop", tag: "resize_observer_loop" },
	{ pattern: "Script error", tag: "cross_origin_script" },
];

function findHarmlessError(error: unknown, fallbackMessage?: string): string | null {
	const msg = error instanceof Error ? error.message : typeof error === "string" ? error : fallbackMessage || "";
	if (!msg) return null;
	return HARMLESS_ERROR_PATTERNS.find((p) => msg.includes(p.pattern))?.tag ?? null;
}

export const initErrorHandlers = (app: App): void => {
	// Глобальные ошибки от vue
	app.config.errorHandler = (err, _instance, info) => {
		if (isChunkError(err)) {
			handleChunkError("vue.errorHandler", err);
			return;
		}

		const harmlessTag = findHarmlessError(err);
		if (harmlessTag) {
			Sentry.captureException(err, {
				tags: { type: harmlessTag, source: "vue.errorHandler" },
				level: "info",
			});
			return;
		}

		console.error("[QR] Vue Error:", err, info);
		Sentry.captureException(err, {
			extra: { info },
			tags: { source: "vue.errorHandler" },
		});
	};

	// Глобальные ошибки от промисов (fetch, async errors)
	window.addEventListener("unhandledrejection", (event) => {
		if (isChunkError(event.reason)) {
			handleChunkError("unhandledrejection", event.reason);
			event.preventDefault();
			return;
		}

		const harmlessTag = findHarmlessError(event.reason);
		if (harmlessTag) {
			Sentry.captureException(event.reason, {
				tags: { type: harmlessTag, source: "unhandledrejection" },
				level: "info",
			});
			event.preventDefault();
			return;
		}

		console.error("[QR] Unhandled Rejection:", event.reason);
		Sentry.captureException(event.reason, {
			tags: { source: "unhandledrejection" },
		});
		event.preventDefault();
	});

	// Глобальные ошибки от js (script errors, runtime exceptions)
	window.addEventListener("error", (event) => {
		if (isChunkError(event.error)) {
			handleChunkError("window.error", event.error);
			return;
		}

		const harmlessTag = findHarmlessError(event.error, event.message);
		if (harmlessTag) {
			Sentry.captureException(event.error ?? new Error(event.message || harmlessTag), {
				tags: { type: harmlessTag, source: "window.error" },
				level: "info",
			});
			return;
		}

		if (!event.error && !event.message) return;

		console.error("[QR] Window Error:", event.error ?? event.message);
		Sentry.captureException(event.error ?? new Error(event.message), {
			tags: { source: "window.error" },
		});
	});
};

export const isChunkError = (error: unknown): boolean => {
	if (!error) return false;

	if (error instanceof Error) {
		if (error.name === "ChunkLoadError") return true;
		return CHUNK_ERROR_PATTERNS.some((pattern) => error.message?.includes(pattern));
	}

	if (typeof error === "string") {
		return CHUNK_ERROR_PATTERNS.some((pattern) => error.includes(pattern));
	}

	return false;
};

export const handleChunkError = (source: string, error?: unknown): void => {
	Sentry.captureException(error ?? new Error("Chunk load failed"), {
		tags: { type: "chunk_load_error", source },
		level: "warning",
	});

	if (isPreviewMode) return;

	const last = sessionStorage.getItem(RELOAD_KEY);
	if (!last || Date.now() - Number(last) > RELOAD_COOLDOWN_MS) {
		console.warn(`[QR] Chunk load error (${source}), reloading...`);
		sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
		window.location.reload();
	} else {
		console.warn(`[QR] Chunk load error (${source}), reload skipped cooldown`);
	}
};
